/**
 * MenuPlanningPage Component
 * 
 * Main page for menu planning feature - orchestrates all sub-components.
 * Premium feature gated to unlimited/admin/beta/family users.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RecipeWithTiming, ScheduledSegment, Conflict, ScheduleResult } from '../types/menuPlanning';
import { validateSchedule } from '../utils/scheduler';
import { DayOccasionSelector } from '../components/menuPlanning/DayOccasionSelector';
import TimelineCanvas from '../components/menuPlanning/TimelineCanvas';
import menuPlanningService from '../services/menuPlanningService';
import { getSubscriptionStatus } from '../services/subscriptionService';
import { useAuth } from '../context/authContext';
import PremiumPlanningShell from '../components/PremiumPlanningShell';
import '../styles/menuPlanning.css';

// API Response Types
interface FeatureStatusResponse {
  enabled: boolean;
}

interface RecipeSegmentsResponse {
  totals: {
    activeSec: number;
    inactiveSec: number;
  };
}

interface ScheduleResponse {
  segments: ScheduledSegment[];
  makespan_sec: number;
  active_total_sec: number;
  inactive_total_sec: number;
}

interface ValidationResponse {
  valid: boolean;
  conflicts: Conflict[];
}

export default function MenuPlanningPage() {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [subscription, setSubscription] = useState<any>(null);
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [selectedRecipeIds, setSelectedRecipeIds] = useState<number[]>([]);
  const [selectedSource, setSelectedSource] = useState<{ type: 'occasion' | null, data: any } | null>(null);
  const [recipes, setRecipes] = useState<RecipeWithTiming[]>([]);
  const [scheduleResult, setScheduleResult] = useState<ScheduleResult | null>(null);
  const [conflicts, setConflicts] = useState<Conflict[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [priority, setPriority] = useState<'recipeOrder' | 'shortestActiveFirst'>('recipeOrder');

  // Get planning theme from user profile or localStorage
  const planningTheme = userProfile?.session_preferences?.planning_theme || 
                        localStorage.getItem('planning_theme') || 
                        'tomato';

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const status = await getSubscriptionStatus() as any;
        setSubscription(status);
        
        // Check if user has premium access (unlimited, admin, beta, family only - NOT basic)
        const isPremium = ['unlimited', 'admin', 'beta', 'family'].includes(status.planType);
        setHasAccess(isPremium);
        
        if (!isPremium) {
          // Redirect to subscription page if not premium
          navigate('/profile?tab=subscription');
          return;
        }
      } catch (error) {
        console.error('Failed to fetch subscription status:', error);
        setHasAccess(false);
        navigate('/profile?tab=subscription');
      }
    };

    checkAccess();
  }, [navigate]);

  useEffect(() => {
    // Reset conflicts when schedule changes
    if (scheduleResult) {
      const validation = validateSchedule(scheduleResult.segments);
      setConflicts(validation.conflicts);
    }
  }, [scheduleResult]);


  const handleSelectionChange = async (recipeIds: number[], source: { type: 'occasion', data: any }) => {
    setSelectedRecipeIds(recipeIds);
    setSelectedSource(source);
    // Clear schedule when selection changes
    setScheduleResult(null);
    setRecipes([]);
    setError(null);
    
    // If an occasion is selected, check if a menu plan already exists for it
    if (source.type === 'occasion' && source.data?.id && user) {
      try {
        const token = await user.getIdToken();
        const existingPlan: any = await menuPlanningService.getMenuPlanByOccasion(source.data.id, token);
        
        if (existingPlan) {
          // Menu plan already exists - redirect to it
          console.log('[MenuPlanning] Menu plan already exists for this occasion, redirecting...');
          navigate('/schedule', {
            state: {
              schedule: {
                recipes: (existingPlan.recipe_ids || []).map((id: number, idx: number) => ({
                  id,
                  name: (existingPlan.recipe_names || [])[idx] || `Recipe ${id}`
                })),
                segments: existingPlan.schedule_data?.segments || [],
                makespanSec: existingPlan.makespan_sec || existingPlan.makespanSec || 0,
                activeTotalSec: existingPlan.schedule_data?.active_total_sec || 0,
                inactiveTotalSec: existingPlan.schedule_data?.inactive_total_sec || 0
              },
              occasionId: source.data.id,
              menuPlanId: existingPlan.id
            }
          });
          return;
        }
      } catch (err) {
        console.error('Error checking for existing menu plan:', err);
        // Continue with normal flow if check fails
      }
    }
  };

  const handleGenerateSchedule = async () => {
    if (selectedRecipeIds.length === 0) {
      setError('Please select a day or occasion with scheduled meals');
      return;
    }

    if (!selectedSource) {
      setError('Please select a day or occasion');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Get Firebase token
      const token = await user.getIdToken();
      
      // Fetch recipe details with timing data
      const recipePromises = selectedRecipeIds.map(async (id) => {
        try {
          const segmentData = await menuPlanningService.getRecipeSegments(id, token) as RecipeSegmentsResponse;
          return {
            id,
            activeTotalSec: segmentData.totals?.activeSec || 0,
            inactiveTotalSec: segmentData.totals?.inactiveSec || 0
          };
        } catch (err) {
          console.warn(`Could not fetch segments for recipe ${id}`, err);
          return { id, activeTotalSec: 0, inactiveTotalSec: 0 };
        }
      });
      
      const timingData = await Promise.all(recipePromises);
      
      // Generate schedule from backend
      const result = await menuPlanningService.scheduleRecipes(selectedRecipeIds, { priority }, token) as ScheduleResponse;
      
      // Merge timing data with recipe info
      // Get recipe names from source data if available
      const getRecipeName = (id: number): string => {
        if (selectedSource.type === 'occasion' && selectedSource.data?.occasion_recipes) {
          const or = selectedSource.data.occasion_recipes.find((or: any) => or.recipe_id === id);
          if (or?.recipe_name) return or.recipe_name;
        }
        // Fallback to segment data
        return result.segments.find(s => s.recipeId === id)?.recipeName || `Recipe ${id}`;
      };

      const recipesWithTiming: RecipeWithTiming[] = selectedRecipeIds.map((id) => {
        const timing = timingData.find(t => t.id === id);
        const recipeName = getRecipeName(id);
        
        return {
          id,
          name: recipeName,
          images: [],
          activeTotalSec: timing?.activeTotalSec || 0,
          inactiveTotalSec: timing?.inactiveTotalSec || 0
        };
      });
      
      setRecipes(recipesWithTiming);
      setScheduleResult({
        segments: result.segments,
        makespanSec: result.makespan_sec,
        activeTotalSec: result.active_total_sec,
        inactiveTotalSec: result.inactive_total_sec || 0
      });
    } catch (err: any) {
      console.error('Error generating schedule:', err);
      if (err.response?.status === 401) {
        setError('Authentication failed. Please log out and log back in, then try again.');
      } else {
        setError(err.response?.data?.message || err.message || 'Failed to generate schedule');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSegmentUpdate = async (segmentId: string, newStartSec: number, newDurationSec: number) => {
    if (!scheduleResult) return;

    // Update segment in local state (optimistic)
    const updatedSegments = scheduleResult.segments.map(seg => {
      if (seg.id === segmentId) {
        return {
          ...seg,
          startSec: newStartSec,
          endSec: newStartSec + seg.durationSec
        };
      }
      return seg;
    });

    // Validate the new schedule
    try {
      const token = await user.getIdToken();
      const validation = await menuPlanningService.validateSchedule(updatedSegments, token) as ValidationResponse;
      
      if (validation.valid) {
        setScheduleResult({
          ...scheduleResult,
          segments: updatedSegments
        });
        setConflicts([]);
      } else {
        // Revert on conflict
        setConflicts(validation.conflicts);
        setError('Cannot move segment: would create conflict');
        
        // Clear error after 3 seconds
        setTimeout(() => setError(null), 3000);
      }
    } catch (err: any) {
      console.error('Error validating schedule:', err);
      setError('Validation failed');
    }
  };

  const handleClearSchedule = () => {
    setScheduleResult(null);
    setConflicts([]);
    setError(null);
  };

  const handleSaveSchedule = async () => {
    if (!scheduleResult || !selectedSource) {
      console.log('[MenuPlanning] Cannot save: no schedule result or source selected');
      return;
    }

    try {
      setLoading(true);
      const token = await user.getIdToken();
      
      const occasionName = selectedSource.data?.name || 'Menu Plan';
      const occasionId = selectedSource.data?.id || null;
      
      // If an occasion is selected, check if a menu plan already exists
      if (occasionId) {
        const existingPlan: any = await menuPlanningService.getMenuPlanByOccasion(occasionId, token);
        if (existingPlan) {
          // Menu plan already exists - redirect to it instead of creating a new one
          console.log('[MenuPlanning] Menu plan already exists for this occasion, redirecting...');
          navigate('/schedule', {
            state: {
              schedule: {
                recipes: (existingPlan.recipe_ids || []).map((id: number, idx: number) => ({
                  id,
                  name: (existingPlan.recipe_names || [])[idx] || `Recipe ${id}`
                })),
                segments: existingPlan.schedule_data?.segments || [],
                makespanSec: existingPlan.makespan_sec || existingPlan.makespanSec || 0,
                activeTotalSec: existingPlan.schedule_data?.active_total_sec || 0,
                inactiveTotalSec: existingPlan.schedule_data?.inactive_total_sec || 0
              },
              occasionId: occasionId,
              menuPlanId: existingPlan.id
            }
          });
          setLoading(false);
          return;
        }
      }
      
      // Prompt user to rename the menu plan (default to occasion name)
      const userInput = prompt(`Enter a name for this menu plan:\n(Leave blank to use: "${occasionName}")`);
      
      // If user clicks Cancel, userInput is null
      if (userInput === null) {
        console.log('[MenuPlanning] User cancelled plan save');
        setLoading(false);
        return;
      }
      
      // Use trimmed input or fallback to occasion name
      const planName = userInput.trim() || occasionName;
      
      console.log('[MenuPlanning] Saving plan:', { planName, occasionId, recipeCount: selectedRecipeIds.length });
      
      const savedPlan: { id?: string; name?: string } = await menuPlanningService.saveMenuPlan(
        planName,
        selectedRecipeIds,
        scheduleResult,
        token,
        occasionId || undefined
      );

      console.log('[MenuPlanning] Plan saved:', savedPlan);

      // Dispatch event to notify Planning Hub
      window.dispatchEvent(new CustomEvent('menuPlanCreated', {
        detail: { planId: savedPlan?.id, occasionId: occasionId }
      }));

      // Navigate to schedule page with the saved plan
      console.log('[MenuPlanning] Navigating to /schedule');
      navigate('/schedule', {
        state: {
          schedule: {
            recipes: recipes.map(r => ({ id: r.id, name: r.name })),
            segments: scheduleResult.segments,
            makespanSec: scheduleResult.makespanSec,
            activeTotalSec: scheduleResult.activeTotalSec,
            inactiveTotalSec: scheduleResult.inactiveTotalSec
          },
          occasionId: occasionId,
          menuPlanId: savedPlan?.id
        }
      });
    } catch (err: any) {
      console.error('[MenuPlanning] Error saving menu plan:', err);
      alert(err.response?.data?.message || err.message || 'Failed to save menu plan');
    } finally {
      setLoading(false);
    }
  };

  // Loading/access check
  if (hasAccess === null) {
    return (
      <div style={{ padding: '48px', textAlign: 'center' }}>
        <div className="menu-planning-loading">
          <div className="menu-planning-spinner" />
          Checking access...
        </div>
      </div>
    );
  }

  if (hasAccess === false) {
    return (
      <div style={{ padding: '48px', maxWidth: '600px', margin: '0 auto' }}>
        <div style={{ 
          background: '#FFFFFF', 
          border: '1px solid #0D0D0D', 
          padding: '32px',
          textAlign: 'center'
        }}>
          <h1 style={{ 
            fontFamily: 'system-ui', 
            fontSize: '28px', 
            fontWeight: 500, 
            color: '#1b263b',
            marginBottom: '16px'
          }}>
            Premium Feature
          </h1>
          <p style={{ 
            fontFamily: 'system-ui', 
            fontSize: '16px', 
            color: '#0d1b2a',
            marginBottom: '24px'
          }}>
            Menu Planning is available for Unlimited, Beta, and Family plan members.
          </p>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '24px' }}>
            Redirecting to subscription page...
          </p>
          <button
            onClick={() => navigate('/subscription')}
            className="menu-planning-button"
          >
            Upgrade Now
          </button>
        </div>
      </div>
    );
  }

  return (
    <PremiumPlanningShell theme={planningTheme}>
      <div className="px-8 py-6">
        <div className="w-full">
          <div className="bg-neutral-0 p-4 hairline mb-4">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="font-sans text-3xl font-medium text-[#1b263b]">
                Menu Planning
              </h1>
              <span className="px-2 py-1 bg-[#1b263b] text-neutral-0 font-mono text-xs uppercase tracking-wider">PREMIUM</span>
            </div>
            <p className="font-sans text-sm text-neutral-900 opacity-60">
              Schedule multiple recipes with intelligent timeline coordination
            </p>
          </div>

          <DayOccasionSelector
            onSelectionChange={handleSelectionChange}
            selectedSource={selectedSource}
          />

          <div className="bg-neutral-0 p-4 hairline mb-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <button
                  onClick={handleGenerateSchedule}
                  disabled={selectedRecipeIds.length === 0 || loading || !selectedSource}
                  className="px-4 py-2 font-mono text-sm uppercase tracking-wider bg-[#1b263b] text-white hover:opacity-60 transition-opacity border-none disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Generating...' : 'Generate Schedule'}
                </button>
                
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value as any)}
                  className="px-4 py-2 font-mono text-xs uppercase tracking-wider hairline bg-neutral-50 text-neutral-900"
                >
                  <option value="recipeOrder">Recipe Order</option>
                  <option value="shortestActiveFirst">Shortest Active First</option>
                </select>
              </div>

              {scheduleResult && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleSaveSchedule}
                    className="px-4 py-2 font-mono text-sm uppercase tracking-wider bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-all"
                  >
                    Save Plan
                  </button>
                  <button
                    onClick={handleClearSchedule}
                    className="px-4 py-2 font-mono text-sm uppercase tracking-wider bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-all"
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className="bg-red-600 text-white p-4 hairline mb-4 font-mono text-xs uppercase tracking-wider">
              {error}
            </div>
          )}

          {scheduleResult && (
            <TimelineCanvas
              recipes={recipes}
              segments={scheduleResult.segments}
              conflicts={conflicts}
              onSegmentUpdate={handleSegmentUpdate}
            />
          )}
        </div>
      </div>
    </PremiumPlanningShell>
  );
}

